/**
 * RAS / TemplatePicker — 14 pre-built template cards plus ad-hoc builder entry.
 */

import type { ReactNode } from 'react';
import { Pill } from '@/components/primitives';

export type RasTemplateCategory = 'core' | 'operational' | 'adhoc';

export interface RasTemplate {
  code: string;
  label: string;
  blurb: string;
  category: RasTemplateCategory;
  /** Output formats available for the template. */
  formats: ReadonlyArray<'PDF' | 'CSV' | 'XML'>;
}

/** Locked 14 templates + 1 ad-hoc builder per modules digest §RAS. */
export const RAS_TEMPLATES: RasTemplate[] = [
  { code: 'BSA-AML-SAMPLE', label: 'BSA-AML sample package', blurb: 'Random-sample audit bundle.', category: 'core', formats: ['PDF', 'CSV'] },
  { code: 'CIP-COMPLIANCE', label: 'CIP compliance', blurb: 'Customer Identification Program completeness.', category: 'core', formats: ['PDF', 'CSV'] },
  { code: 'CTR-QUARTERLY', label: 'CTR filings (quarterly)', blurb: 'FinCEN CTR submissions for the quarter.', category: 'core', formats: ['PDF', 'CSV'] },
  { code: 'SAR-LOG', label: 'SAR log', blurb: 'Suspicious Activity Report log.', category: 'core', formats: ['PDF', 'CSV'] },
  { code: 'OFAC-HITS', label: 'OFAC hit log', blurb: 'OFAC screening hits + dispositions.', category: 'core', formats: ['PDF', 'CSV'] },
  { code: 'ADVERSE-30', label: 'Adverse Action 30-day clock', blurb: 'Reg B deadline adherence.', category: 'core', formats: ['PDF', 'CSV'] },
  { code: 'IRA-OPENS', label: 'IRA opens in period', blurb: 'New IRA account opens.', category: 'core', formats: ['PDF', 'CSV'] },
  { code: 'ESCH-NAUPA', label: 'Escheatment candidates (NAUPA XML)', blurb: 'NAUPA Standard Electronic XML export.', category: 'core', formats: ['XML', 'PDF'] },
  { code: 'DORMANT-ACCTS', label: 'Dormant accounts', blurb: 'Accounts approaching dormancy.', category: 'operational', formats: ['PDF', 'CSV'] },
  { code: 'SDB-CLOSEOUTS', label: 'SDB close-outs', blurb: 'Box close-outs during period.', category: 'operational', formats: ['PDF', 'CSV'] },
  { code: 'DEATH-NOTICES', label: 'Death notices', blurb: 'Deceased-customer workflow activity.', category: 'operational', formats: ['PDF', 'CSV'] },
  { code: 'OVERRIDE-LOG', label: 'Override log', blurb: 'Manager + Supervisor overrides.', category: 'operational', formats: ['PDF', 'CSV'] },
  { code: 'KILLLIST-ACT', label: 'Kill List activity', blurb: 'Verify/execute/cancel events.', category: 'operational', formats: ['PDF', 'CSV'] },
  { code: 'PIPELINE-ABND', label: 'Pipeline abandonment', blurb: 'Cart abandonment + staleness.', category: 'operational', formats: ['PDF', 'CSV'] },
  { code: 'ADHOC', label: 'Ad-hoc query builder', blurb: 'Guided field picker → filter builder → preview.', category: 'adhoc', formats: ['PDF', 'CSV'] },
];

export interface TemplatePickerProps {
  selected?: string;
  onSelect: (code: string) => void;
  children?: ReactNode;
}

export function TemplatePicker({ selected, onSelect }: TemplatePickerProps): JSX.Element {
  const core = RAS_TEMPLATES.filter((t) => t.category === 'core');
  const ops = RAS_TEMPLATES.filter((t) => t.category === 'operational');
  const adhoc = RAS_TEMPLATES.filter((t) => t.category === 'adhoc');

  return (
    <div style={{ display: 'grid', gap: 'var(--sp-7)' }}>
      <Group label={`Core regulatory (${core.length})`} templates={core} selected={selected} onSelect={onSelect} />
      <Group label={`Operational (${ops.length})`} templates={ops} selected={selected} onSelect={onSelect} />
      <Group label="Ad-hoc" templates={adhoc} selected={selected} onSelect={onSelect} />
    </div>
  );
}

function Group({
  label,
  templates,
  selected,
  onSelect,
}: {
  label: string;
  templates: RasTemplate[];
  selected?: string;
  onSelect: (code: string) => void;
}): JSX.Element {
  return (
    <section>
      <h3
        style={{
          margin: '0 0 var(--sp-4)',
          fontSize: 12,
          textTransform: 'uppercase',
          letterSpacing: '0.6px',
          color: 'var(--ink-500)',
        }}
      >
        {label}
      </h3>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
          gap: 'var(--sp-4)',
        }}
      >
        {templates.map((t) => {
          const active = selected === t.code;
          return (
            <button
              key={t.code}
              type="button"
              onClick={() => onSelect(t.code)}
              style={{
                textAlign: 'left',
                padding: 'var(--sp-5)',
                background: active ? 'var(--info-bg)' : 'var(--surface-card)',
                border: `1px solid ${active ? 'var(--pcb-blue)' : 'var(--surface-border)'}`,
                borderRadius: 'var(--r-md)',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--sp-2)',
                fontFamily: 'var(--font-sans)',
              }}
            >
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--pcb-blue)' }}>{t.code}</div>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{t.label}</div>
              <div style={{ fontSize: 12, color: 'var(--ink-600)' }}>{t.blurb}</div>
              <div style={{ display: 'flex', gap: 'var(--sp-2)', marginTop: 'var(--sp-2)' }}>
                {t.formats.map((f) => (
                  <Pill key={f} variant="muted">
                    {f}
                  </Pill>
                ))}
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
}
